import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Save, Eye, EyeOff } from "lucide-react";
import {
  fetchAdminPosts,
  fetchAdminPost,
  createPost,
  updatePost,
  deletePost,
} from "../lib/api";

const blank = { title: "", slug: "", excerpt: "", body: "", cover_image: "", published: false };

export default function AdminBlogPage() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null); // null = list; object = form
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const items = await fetchAdminPosts();
      setPosts(items);
    } catch (e) {
      if (e.response?.status === 401) navigate("/admin/login");
      else toast.error("Failed to load posts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("gr_admin_token")) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [navigate]);

  const onEdit = async (id) => {
    try {
      const p = await fetchAdminPost(id);
      setEditing(p);
    } catch {
      toast.error("Failed to load post");
    }
  };

  const onCreate = () => setEditing({ ...blank });

  const onSave = async () => {
    if (!editing.title || !editing.body) {
      toast.error("Title and body are required");
      return;
    }
    setSaving(true);
    try {
      if (editing.id) {
        await updatePost(editing.id, editing);
        toast.success("Post updated");
      } else {
        await createPost(editing);
        toast.success("Post created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onTogglePublish = async (p) => {
    try {
      await updatePost(p.id, { published: !p.published });
      toast.success(p.published ? "Unpublished" : "Published");
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const onDelete = async (p) => {
    if (!window.confirm(`Delete "${p.title}" permanently?`)) return;
    try {
      await deletePost(p.id);
      toast.success("Post deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-blog-page">
      <header className="bg-forest text-cream">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/admin")}
              className="text-cream/70 hover:text-cream flex items-center gap-2 text-sm"
              data-testid="back-to-admin"
            >
              <ArrowLeft size={14} /> Admin
            </button>
            <span className="text-cream/30">/</span>
            <div className="font-serif text-xl">Blog</div>
          </div>
          {!editing && (
            <button
              onClick={onCreate}
              data-testid="new-post-btn"
              className="bg-cream text-forest px-4 py-2 text-sm hover:bg-gold hover:text-cream transition-colors inline-flex items-center gap-2"
            >
              <Plus size={14} /> New Post
            </button>
          )}
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        {editing ? (
          <PostEditor
            value={editing}
            onChange={setEditing}
            onCancel={() => setEditing(null)}
            onSave={onSave}
            saving={saving}
          />
        ) : (
          <PostList
            posts={posts}
            loading={loading}
            onEdit={onEdit}
            onDelete={onDelete}
            onTogglePublish={onTogglePublish}
          />
        )}
      </main>
    </div>
  );
}

const PostList = ({ posts, loading, onEdit, onDelete, onTogglePublish }) => (
  <div className="bg-white border border-bordersoft">
    {loading && <div className="p-8 text-graphite">Loading…</div>}
    {!loading && posts.length === 0 && (
      <div className="p-12 text-center text-graphite text-sm" data-testid="no-posts">
        No posts yet. Click "New Post" to publish your first insight.
      </div>
    )}
    {posts.map((p) => (
      <div
        key={p.id}
        className="px-6 py-5 border-b border-bordersoft last:border-b-0 flex items-center justify-between gap-4"
        data-testid={`post-row-${p.slug}`}
      >
        <button onClick={() => onEdit(p.id)} className="text-left flex-1">
          <div className="flex items-center gap-3">
            <div className="font-serif text-xl text-ink">{p.title}</div>
            <span
              className={`text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 ${
                p.published ? "bg-forest text-cream" : "bg-bordersoft text-graphite"
              }`}
            >
              {p.published ? "Published" : "Draft"}
            </span>
          </div>
          <div className="text-graphite text-sm mt-1">/{p.slug} · {new Date(p.created_at).toLocaleDateString()}</div>
        </button>
        <div className="flex gap-2 shrink-0">
          <button
            onClick={() => onTogglePublish(p)}
            data-testid={`toggle-publish-${p.slug}`}
            className="p-2 text-graphite hover:text-forest transition"
            aria-label={p.published ? "Unpublish" : "Publish"}
          >
            {p.published ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
          <button
            onClick={() => onDelete(p)}
            data-testid={`delete-post-${p.slug}`}
            className="p-2 text-graphite hover:text-red-600 transition"
            aria-label="Delete"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    ))}
  </div>
);

const PostEditor = ({ value, onChange, onCancel, onSave, saving }) => {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  return (
    <div className="bg-white border border-bordersoft p-8" data-testid="post-editor">
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-serif text-3xl text-forest">
          {value.id ? "Edit post" : "New post"}
        </h2>
        <button
          onClick={onCancel}
          data-testid="cancel-edit-btn"
          className="text-sm text-graphite hover:text-forest"
        >
          Cancel
        </button>
      </div>
      <div className="space-y-5">
        <Field label="Title *" value={value.title} onChange={set("title")} testId="post-title-input" />
        <Field label="Slug (auto from title if blank)" value={value.slug} onChange={set("slug")} testId="post-slug-input" />
        <Field label="Excerpt" value={value.excerpt} onChange={set("excerpt")} testId="post-excerpt-input" />
        <Field label="Cover image URL" value={value.cover_image} onChange={set("cover_image")} testId="post-cover-input" />
        <div>
          <label className="uppercase text-[10px] tracking-[0.25em] text-sage mb-2 block">
            Body (Markdown) *
          </label>
          <textarea
            rows={16}
            value={value.body}
            onChange={set("body")}
            data-testid="post-body-input"
            className="w-full bg-cream border border-bordersoft px-4 py-3 text-ink text-sm font-mono focus:outline-none focus:border-forest"
            placeholder="## Section heading

Write here using markdown — **bold**, *italic*, [links](https://example.com), and:
- bullet points
- like this"
          />
        </div>
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={!!value.published}
            onChange={(e) => onChange({ ...value, published: e.target.checked })}
            data-testid="post-publish-checkbox"
            className="w-4 h-4"
          />
          <span className="text-sm text-ink">Published (visible on /blog)</span>
        </label>
        <button
          onClick={onSave}
          disabled={saving}
          data-testid="save-post-btn"
          className="bg-forest text-cream px-7 py-3 text-sm tracking-wider hover:bg-forest/90 transition disabled:opacity-60 inline-flex items-center gap-2"
        >
          <Save size={14} />
          {saving ? "Saving…" : "Save post"}
        </button>
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, testId }) => (
  <div>
    <label className="uppercase text-[10px] tracking-[0.25em] text-sage mb-2 block">{label}</label>
    <input
      type="text"
      value={value || ""}
      onChange={onChange}
      data-testid={testId}
      className="w-full bg-cream border border-bordersoft px-4 py-3 text-ink text-sm focus:outline-none focus:border-forest"
    />
  </div>
);
